import s from "./style.module.scss";
import { PageWrapper } from "../PageWrapper";
import { MoneyBack } from "../Icons/MoneyBack";
import GuaranteedCheckout from "./GuaranteedCheckout";
import OrderNowPayLater from "./Components/OrderNowPayLater";
import { useSectionsData } from "../../utils/useSectionsData.ts";
import { getBrandName } from "../../utils/helpers.ts";
import axios from "axios";
import { ProductCard } from "./Components/ProductCard.tsx";
import {
  CardContent,
  Product,
  ProductBundleItem,
  CampaignData,
} from "../../types/products.ts";

interface ProductPickerProps {
  setIsLoading: (loading: boolean) => void;
}

export const ProductPicker = ({ setIsLoading }: ProductPickerProps) => {
  const brandName = getBrandName();
  const sections = useSectionsData();
  const productPickerSection = sections?.pp_v7_product_picker_section;
  const orderNowLabel = productPickerSection?.tr_order_now_label;
  const refundLabel = productPickerSection?.tr_refund_policy_label;
  const refundText = productPickerSection?.tr_refund_policy_text;
  const guaranteedCheckout = productPickerSection?.tr_guaranteed_safe_checkout;
  const ctaLink = window?.cta_link;
  const formattedString = window.formattedString;
  const productsData = window.productData;
  const products: Product[] = productsData?.products || [];
  const cardContents: CardContent[] = productPickerSection?.product_cards;
  const combinedCards = cardContents.map((content, index) => {
    const product = products[index] || {};

    return {
      ...content,
      img: product.image,
      price: product.price,
      oldPrice: product.old_price,
      bestValueStatus: product.best_seller,
      label: product.status,
      isBestValue: product.best_seller,
    };
  });

  const handleClick = async (selectedProduct: Product) => {
    try {
      setIsLoading(true);

      let bundleProducts: ProductBundleItem[] = [];

      if (
        selectedProduct.bundle_products &&
        selectedProduct.bundle_products.length > 0
      ) {
        bundleProducts = selectedProduct.bundle_products;
      }

      const cartItem = {
        bundle_products: bundleProducts,
        id: selectedProduct.id || crypto.randomUUID(),
        status: selectedProduct.status,
        amount: selectedProduct.amount,
        price: selectedProduct.price,
        old_price: selectedProduct.old_price,
        you_save_percentage: selectedProduct.you_save_percentage,
        shipping_price: selectedProduct.shipping_price || "0",
        display_name: selectedProduct.display_name,
        description: selectedProduct.tr_additional_texts,
        generic_name: selectedProduct.generic_name,
        image: selectedProduct.image,
        best_seller: selectedProduct.best_seller || false,
        best_value: selectedProduct.best_seller || false,
        enable_fake_products: selectedProduct.enable_fake_products || false,
        fake_products: selectedProduct.fake_products || false,
      };

      const campaignData = {
        campaign_id: productsData?.konnektive_campaign_id,
        locale: productsData?.locale,
        next_page_on_success: productsData?.next_page_on_success,
        currency_code: productsData?.currency_code,
        enable_3ds_waterfall: productsData?.enable_3ds_waterfall,
        three_ds_waterfall_campaign_id:
          productsData?.three_ds_waterfall_campaign_id,
      };

      const warrantyData = {
        display_name: productsData?.select_warranty_type_cc.label,
        enabled: productsData?.add_warranty_for_product,
        preselect: productsData?.preselect_warranty,
        sku: productsData?.select_warranty_type_cc.value,
        konnektive_id: selectedProduct.warranty_product_id,
        multiply_warranty: productsData?.multiply_price_by_amount,
        price: selectedProduct.warranty_price,
        warrantyState: false,
        amount: selectedProduct.amount,
      };

      const payload = {
        cartProducts: [cartItem],
        country: window.country,
        warrantyData: warrantyData,
        formattedString: formattedString,
        campaignData: campaignData,
      };

      const url = "/wp-json/brands/cart/v1/session";
      const response = await axios.post(
        url,
        { payload: payload },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      function getRawParam(param: string) {
        const search = window.location.search.substring(1);
        const params = search.split("&");
        for (let p of params) {
          if (p.startsWith(param + "=")) {
            return p.substring(param.length + 1);
          }
        }
        return null;
      }

      const googleClickId = getRawParam("_gl");

      if (response.status === 200 && response.data?.session_hash) {
        const urlSymbol = ctaLink?.includes("?") ? "&" : "?";
        window.location.href = `${ctaLink}${urlSymbol}cart=${
          response.data.session_hash
        }&aff=${window.btoa(window.affiliate || "")}&entryUrl=${window.btoa(
          window.entry_url || ""
        )}&postId=remote${googleClickId ? `&_gl=${googleClickId}` : ""}`;
      } else {
        console.error(
          "API Error or missing session hash:",
          response.status,
          response.data
        );
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error creating session:", error);
      setIsLoading(false);
    }
  };

  return (
    <>
      <PageWrapper backgroundColor="section_1">
        <div className={s.productPicker__wrapper}>
          <div className={s.productPicker} id="products">
            {productsData &&
              combinedCards.map((card, index) => (
                <ProductCard
                  key={index}
                  card={card}
                  product={products[index]}
                  index={index}
                  brandName={brandName}
                  formattedString={formattedString}
                  productPickerSection={productPickerSection}
                  combinedCards={combinedCards}
                  onCardClick={handleClick}
                />
              ))}
          </div>
          <div className={s.refund__wrapper}>
            <OrderNowPayLater text={orderNowLabel} />
            <div className={s.refund__orderRow__methods}>
              <div className={s.refund__orderRow__moneyBack}>
                <MoneyBack />
              </div>
              <div className={s.refund__orderRow__texts}>
                <div className={s.refund__orderRow__policy}>{refundLabel}</div>
                <p dangerouslySetInnerHTML={{ __html: refundText }} />
              </div>
              <GuaranteedCheckout text={guaranteedCheckout} />
            </div>
          </div>
        </div>
      </PageWrapper>
    </>
  );
};
